import { CroppingEditOptions, CroppingMultipleTargetUpdateOptions, CroppingUpdateModel, getAuthorizedCroppingFilter } from "@omnia/shared/fields"
import { convertDatesToJsonUtc, createMap, FieldViewModel, FieldViewModelImpl, getDistinctValues, getParameters, ICropping, ICroppingYearModel, IField, IFieldFilter, IOmniaSession, IOmniaSettingsKey, isArray, isArrayEmpty, isObject, numericTextSort, OmniaModuleId, OmniaObjectType, OmniaService, ParamKey, SharedDataService, str, sum } from "../omnia"
import { CroppingDependencies, FieldDependencies, ICroppingMergeModel, ICroppingSplitModel, IFieldDisableModel, IFieldEditModel, IFieldsService } from "../types"
import { applyFilter } from "./filter"
import { FieldsLoader } from "./loader"
import { isRestrictedMember } from "@omnia/shared/session/utils"
import { getDefaultGeometryFormat } from "@omnia/shared/geom/binary"

export class FieldsService extends OmniaService implements IFieldsService {

    private _loader = new FieldsLoader(
        this.$q,
        this.ohttp,
        this.apiInterface,
        this.sharedData,
        getDefaultGeometryFormat()
    )

    private _mapView: boolean = false
    private _viewSetting: IOmniaSettingsKey

    private _allItems: FieldViewModel[] = []

    private _fields: FieldViewModel[] = []
    private _allFields: FieldViewModel[] = []

    private _cropping: FieldViewModel[] = []
    private _allCropping: FieldViewModel[] = []

    private _currentCropping: FieldViewModel[] = []

    private _selectedItems: FieldViewModel[] = []
    private _selectedFields: FieldViewModel[] = []
    private _selectedCroppings: FieldViewModel[] = []

    private _filter: IFieldFilter

    /**
     * Ids of the cropping "authorized" to the current user
     * All are authorized if undefined
     */
    private _authorizedCropping: number[]

    private _located: { items: any[] } = { items: [] };

    static $inject = ["$q", "$rootScope", "OmniaSession", "OmniaSharedData"]

    constructor(
        private $q: ng.IQService,
        private $rootScope: ng.IRootScopeService,
        session: IOmniaSession,
        private sharedData: SharedDataService
    ) {

        super(session, { name: str.Modules.Fields, module: OmniaModuleId.Fields, dependencies: [sharedData] })
        this.restoreSettings()
    }

    get region() {
        return this.session.holding?.Region
    }

    get timelineView(): boolean {
        return !this._mapView
    }

    get mapView(): boolean {
        return this._mapView
    }

    setView(map: boolean) {
        this._mapView = map
        this._viewSetting.set(map ? "map" : "timeline")
    }

    get crops() {
        return this.sharedData.crops
    }

    get cropVarieties() {
        return this.sharedData.varieties
    }

    get cropStyles() {
        return this.sharedData.cropStyles
    }

    getCropManagementOptions() {
        const { sharedData, session: { holding: { Region: region } } } = this
        return sharedData.getCropManagementOptions(region)
    }

    getParameters(type: ParamKey) {
        return getParameters(type)
    }

    get soilTypes() {
        return this.sharedData.soilTypes
    }

    get subsoilTypes() {
        return this.sharedData.subsoilTypes
    }

    private restoreSettings() {

        this._viewSetting = this.session.getSettings("fields").getKey("view")
        this._mapView = ("map" == this._viewSetting.get("map"))

    }

    shouldRefresh() {

        const changes = this.apiState.getChangesFromSource(
            this._changeNumber,
            s => (s == OmniaModuleId.Fields)
                || (s == OmniaModuleId.Nutrition)
                || (s == OmniaModuleId.Seeds)
                || (s == OmniaModuleId.CropProtection)
                || (s == OmniaModuleId.Imports)
                || (s == OmniaModuleId.Layers)
                || (s == "refresh")
                || (s == "refresh-all")
                || (!s)
        )

        return !isArrayEmpty(changes)

    }

    protected getRefreshPromise(full?: boolean, scope?: any): ng.IPromise<any> {

        const { session, _loader } = this

        const currentSelection = this.getCurrentSelection()

        this.unselectAll()

        const promise = scope && isObject(scope) && isArray(scope["fieldIds"])
            ? _loader.loadFields(scope["fieldIds"])
            : _loader.load(this.session, this.shouldRefresh())

        return promise
            .then(() => this.refreshCroppingPermissions())
            .then(() => this.refreshFieldsAndSelection(currentSelection))

    }

    private reloadFields(ids: number | number[]) {

        const fieldIds = (isArray(ids) ? ids : [ids]).filter(id => !!id)

        this.recordChange({ type: OmniaObjectType.Field, data: fieldIds.map(id => ({ FieldId: id })) }, false)

        return this.refreshInternal(false, { fieldIds })

    }

    private refreshFieldsAndSelection(currentSelection?: string[]) {

        currentSelection = currentSelection || this.getCurrentSelection()

        this.unselectAll()

        this.refreshFields()

        const selection = this._allItems.filter(i => {

            if (!i.visible) return false
            var iid = i.getId()

            return !!currentSelection.find(s => s == iid)

        })

        this.selectItems(selection, true)

    }

    private refreshCroppingPermissions() {

        const { session, _loader } = this

        if (!isRestrictedMember(session)) {
            this._authorizedCropping = undefined
            return
        }

        this._authorizedCropping = []

        return _loader.loadCroppingPermissions(session.yearId)
            .then(permissions => {

                this._authorizedCropping = isArrayEmpty(permissions)
                    ? undefined
                    : permissions.filter(p => p.Authorized).map(p => p.CroppingId)

            })

    }

    private getCurrentSelection() {
        return this._selectedItems.map(i => i.getId())
    }

    private refreshFields() {

        const fields = numericTextSort(this._loader.fields, f => f.Name)

        const authorizedCroppingFilter = getAuthorizedCroppingFilter(this)

        const vm = fields
            .map(field => new FieldViewModelImpl(field, this.session.yearId, authorizedCroppingFilter))
            .map((field, index) => ({ field, index }))
            .sort((f1, f2) => {
                let diff = (f1.field.disabled ? 1 : 0) - (f2.field.disabled ? 1 : 0)
                return diff ? diff : f1.index - f2.index
            })
            .map(f => f.field)

        this._allFields = vm
        this._fields = vm.filter(i => !i.disabled)

        this._allItems = vm.concat(Array.prototype.concat.apply([], vm.map(f => f.croppings)))

        this._allCropping = this._allItems.filter(f => f.isCropping())
        this._cropping = this._allCropping.filter(c => !c.disabled)

        this.applyFilter()

    }

    private applyFilter() {

        const items = this._allFields
        if (!items) return

        const filter = this._filter

        items.forEach(f => {
            applyFilter(f, filter)
        })

        this._currentCropping = this._cropping.filter(c => c.currentYear)

        this.$rootScope.$broadcast("omnia.fields.filter")

    }

    get lastLocatedItems() {
        return this._located
    }

    locateItems(items: FieldViewModel[]) {
        this._located = { items: [...items] };
    }

    getFields(disabled: boolean): FieldViewModel[] {
        return disabled
            ? this._allFields || []
            : this._fields || []
    }

    get fields(): FieldViewModel[] {
        return this._fields || []
    }

    get cropping(): FieldViewModel[] {
        return this._cropping || []
    }

    get currentCropping(): FieldViewModel[] {
        return this._currentCropping
    }

    findField(fieldId: number, includeDisabled: boolean = true): FieldViewModel {
        const fields = includeDisabled ? this._allFields : this._fields
        return fields.find(f => f.field.FieldId == fieldId)
    }

    getFieldProvider(includeDisabled?: boolean): (fieldId: number) => FieldViewModel {
        const fields = includeDisabled ? this._allFields : this._fields
        const map = createMap(fields, f => f.fieldId)
        return (fieldId: number) => map.get(fieldId)
    }

    selectSingleField(fieldId: number): FieldViewModel {
        const c = this.findField(fieldId)
        if (c) this.selectItems([c], true)
        return c
    }

    findCropping(croppingId: number, includeDisabled: boolean = true): FieldViewModel {
        const cropping = includeDisabled ? this._allCropping : this._cropping
        return cropping.find(c => c.croppingId == croppingId)
    }

    getCroppingProvider(includeDisabled?: boolean): (croppingId: number) => FieldViewModel {
        const cropping = includeDisabled ? this._allCropping : this._cropping
        const map = createMap(cropping, c => c.croppingId)
        return (croppingId: number) => map.get(croppingId)
    }

    getAuthorizedCropping(): number[] {
        const { _authorizedCropping: auth } = this
        return auth ? [...auth] : undefined
    }

    selectSingleCropping(croppingId: number): FieldViewModel {
        var c = this.findCropping(croppingId)
        if (c) this.selectItems([c], true)
        return c
    }

    get filter(): IFieldFilter {
        return this._filter
    }

    set filter(value: IFieldFilter) {
        this._filter = value
        this.applyFilter()
    }

    get selectedItems() {
        return this._selectedItems;
    }

    get selectedCroppings() {
        return this._selectedCroppings;
    }

    get selectedFields() {
        return this._selectedFields;
    }

    unselectAll() {
        this._selectedItems.forEach((i) => i.selected = false)
        this.setSelectedItems([]);
    }

    selectItems(items: FieldViewModel[], unselectOthers: boolean) {

        if (this.selectedItems.length == items.length) {
            if (this.selectedItems.every((e) => (items.indexOf(e) >= 0))) {
                return true
            }
        }

        if (unselectOthers) {
            this.unselectAll()
        }

        items.forEach((i) => {
            i.selected = true
        })

        this.setSelectedItems(items)

    }

    private setSelectedItems(items: FieldViewModel[]) {

        this._selectedItems = items.filter(f => this._allItems.indexOf(f) >= 0).slice();

        // Cache pre-filtered lists.
        this._selectedCroppings = this._selectedItems.filter((x) => x.isCropping());
        this._selectedFields = this._selectedItems.filter((x) => x.isField());

    }

    createField(field: IField): ng.IPromise<any> {

        field.FirstYearId = field.FirstYearId || this.yearId

        return this.editFields(null, field)

    }

    updateFields(fieldIds: number[], data: IFieldEditModel): ng.IPromise<any> {

        return this.editFields(fieldIds, data)

    }

    editFields(fieldIds: number[], model: IFieldEditModel): ng.IPromise<any> {

        const { holdingId, yearId } = this

        const data = {
            ...model,
            HoldingId: holdingId,
            SessionHoldingId: holdingId,
            SessionYearId: yearId,
            FieldIds: <number[]>undefined
        }

        let url = ""

        if (fieldIds && fieldIds.length) {
            data.FieldIds = fieldIds
            url = "/api/fields/update/fields"
        }
        else {
            url = "/api/fields/create/field"
        }

        return this.post(url, data, false)
            .then((data: IField[] | IField) => {

                const fieldIds = isArray(data)
                    ? data.map(f => f.FieldId)
                    : [data.FieldId]

                return this.reloadFields(fieldIds)

            })

    }

    editFieldBoundary(fieldId: number, boundary: string): ng.IPromise<any> {

        const { holdingId } = this

        const data = {
            holdingId,
            fieldId,
            boundary
        }

        const url = "/api/fields/update/field-boundary"

        return this.post(url, data, false).then(() => this.reloadFields(fieldId))

    }

    createCropping(data: ICropping): ng.IPromise<any> {

        return this.editCropping(null, data);

    }

    updateCropping(croppingIds: number[], data: ICropping, editOptions?: CroppingEditOptions, multiTargetOptions?: CroppingMultipleTargetUpdateOptions): ng.IPromise<any> {

        return this.editCropping(croppingIds, data, editOptions, multiTargetOptions);

    }

    editCropping(croppingIds: number[], model: ICropping, editOptions?: CroppingEditOptions, multiTargetOptions?: CroppingMultipleTargetUpdateOptions): ng.IPromise<ICropping[]> {

        const { holdingId, yearId } = this

        const data: CroppingUpdateModel = {

            ...model,

            SessionHoldingId: holdingId,
            SessionYearId: yearId,
            YearId: model.YearId || yearId,
            Crop: null,
            CroppingIds: croppingIds,

            EditOptions: {
                CreateOnMultipleYears: true,
                ...editOptions
            },

            MultipleTargetUpdateOptions: multiTargetOptions

        }

        convertDatesToJsonUtc(data)

        const url = croppingIds
            ? "/api/fields/update/cropping"
            : "/api/fields/create/cropping"

        return this.post(url, data, false).then((cropping: ICropping[]) => {

            const fieldIds = getDistinctValues(cropping, c => c.FieldId)

            return this.reloadFields(fieldIds)

        })

    }

    splitCropping(split: ICroppingSplitModel): ng.IPromise<ICropping[]> {

        const url = "/api/fields/split/cropping"

        return this.post(url, split)
            .then(r => {
                this.reloadFields([split.FieldId])
                return r
            })

    }

    mergeCropping(merge: ICroppingMergeModel): ng.IPromise<ICropping> {

        const url = "/api/fields/merge/cropping"

        return this.post(url, merge)
            .then(r => {
                this.reloadFields([merge.FieldId])
                return r
            })

    }

    canDeleteField(id: number): ng.IPromise<FieldDependencies> {
        const url = `/api/fields/delete/field/check?id=${id}`
        return this.ohttp.get(url).then(r => isObject(r.data) ? r.data : undefined)
    }

    deleteField(fieldId: number, admin: boolean): ng.IPromise<any> {

        return this._loader.deleteField(fieldId, admin)
            .then(r => this.refreshFieldsAndSelection())

    }

    canDeleteCropping(ids: number[]): ng.IPromise<CroppingDependencies[]> {

        const url = `/api/fields/delete/cropping/check?ids=${ids.join(",")}`

        return this.ohttp.get(url).then(r => isArray(r.data) ? r.data : undefined)

    }

    deleteCropping(croppingId: number, admin: boolean, layers: "delete" | "realloc"): ng.IPromise<any> {

        const fieldId = getCroppingFieldId(croppingId, this._allCropping)

        const query = [
            admin ? "dependencies=true" : null,
            layers ? "layers=" + layers : null
        ].filter(a => a).join("&")

        const url = "/api/fields/delete/cropping/" + croppingId + (query ? "?" + query : "")

        return this.delete(url, undefined, false).then(() => this.reloadFields([fieldId]))

    }

    setupCroppingYear(model: ICroppingYearModel): ng.IPromise<any> {

        const url = "/api/fields/year/setup"

        return this.post(url, model)

    }

    updateDisabledFields(model: IFieldDisableModel[]): ng.IPromise<any> {

        const url = "/api/fields/disable"

        return this.post(url, model)

    }

    createDisabledCropping(fieldId: number, year: number): ng.IPromise<ICropping> {

        const url = `/api/fields/disable/create-cropping?fieldId=${fieldId}&year=${year}`

        return this.post(url)

    }

    getFieldsArea(): number {
        return sum(this.fields, f => f.area)
    }

}

function getCroppingFieldId(croppingId: number, cropping: FieldViewModel[]) {
    const c = cropping.find(c => c.croppingId == croppingId)
    return c ? c.fieldId : undefined
}
